import {makeAutoObservable, observable} from 'mobx'
import {Lang} from 'yougile-lang'
import {ArrUtil} from 'yougile-util'
import {MessageBox} from '@components/message-box'
import {createUseStore} from '@containers/hooks'
import {PageService} from '@extensions/page-service'
import {authenticateTrello, loadTrelloClient} from './utils/trello-loader'

export interface BoardOption {
  label: string
  value: string
  backgroundImage?: string
  backgroundColor?: string
}

interface State {
  open: boolean
  loading: boolean
  authenticated: boolean
  error: Error | undefined
  fullName: string
  boards: BoardOption[] | undefined
  selectedBoards: string[]
  loadingProgress: number
}

export class ImportFromTrelloStore {
  state: State = {
    open: false,
    loading: false,
    authenticated: window.Trello?.authorized() ?? false,
    error: undefined,
    fullName: '',
    boards: [],
    selectedBoards: [],
    loadingProgress: 0
  }
  setOpen: (state: boolean) => void

  constructor(setOpen: (state: boolean) => void) {
    this.setOpen = setOpen
    makeAutoObservable(
      this,
      {
        state: observable.shallow
      },
      {autoBind: true}
    )
  }

  onError(error: Error): void {
    window.Trello?.deauthorize()
    this.setError(error)
    this.setLoading(false)
    this.setBoards(undefined)
    this.setFullName('')
    this.setAuthenticated(false)
  }

  onSuccess(): void {
    this.setError(undefined)
    this.setLoading(false)
    this.setAuthenticated(true)
  }

  async open(): Promise<void> {
    if (PageService.getCompanySubdomain()) {
      MessageBox.warn(Lang.msg.importFromTrello.warning)
    } else {
      if (!window.Trello) {
        await loadTrelloClient()
      }
      this.setOpen(true)
      if (!window.Trello?.authorized() || !this.state.authenticated) {
        authenticateTrello(this.onSuccess, this.onError)
      }
    }
  }

  setOpenState(open: boolean): void {
    this.state.open = open
    this.state.loadingProgress = 0
  }

  setLoading(loading: boolean): void {
    this.state.loading = loading
  }

  setLoadingProgress(percent: number): void {
    this.state.loadingProgress = Math.floor(percent * 100)
  }

  setAuthenticated(authenticated: boolean): void {
    this.state.authenticated = authenticated
  }

  setError(error: Error | undefined): void {
    this.state.error = error
  }

  setFullName(fullName: string): void {
    this.state.fullName = fullName
  }

  setSelectedBoards(selected: string[]): void {
    this.state.selectedBoards = selected
  }

  setBoards(boards: BoardOption[] | undefined): void {
    this.state.boards = boards
  }

  selectAll(): void {
    this.setSelectedBoards(this.state.boards?.map(b => b.value) ?? [])
  }

  removeSelection(): void {
    this.setSelectedBoards([])
  }

  toggleBoardSelected(board: BoardOption): void {
    if (!this.state.boards) {
      return
    }
    const newValueSet = new Set(
      ArrUtil.xor(this.state.selectedBoards, [board.value])
    )
    const newValue = ArrUtil.filterMap(this.state.boards, board =>
      newValueSet.has(board.value) ? board.value : null
    )
    this.setSelectedBoards(newValue)
  }

  close(): void {
    this.setOpen(false)
    this.setAuthenticated(false)
    this.setError(undefined)
    this.setFullName('')
    this.setBoards(undefined)
  }
}

const [ImportFromTrelloContext, useImportFromTrelloStore] = createUseStore(
  ImportFromTrelloStore
)
export {ImportFromTrelloContext, useImportFromTrelloStore}
